import React, { useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { NativeCategory, NativeTagWithCategory } from 'types/track'
import {
  createCustomNativeTag,
  editCustomNativeTag,
} from '@disco/state-management/actions/CustomNativeTagActions'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getNativeCategories } from '@disco/state-management/selectors/tags'
import ColorIndicatorsAlert from 'components/common/color-indicators-alert'
import { Box } from 'components/ds'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Label from 'components/ui/label'
import ModalDialog from 'components/ui/modal-dialog'
import TextField from 'components/ui/text-field'
type Props = {
  editingTag?: NativeTagWithCategory
  onRequestClose: () => void
}

const TrackTagEditModal = (props: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const { editingTag, onRequestClose } = props
  const nativeCategories: NativeCategory[] = useSelector(getNativeCategories)
  const [name, setName] = useState<string>(editingTag?.name ?? '')
  const [category, setCategory] = useState<number | null | undefined>(
    editingTag ? editingTag.category_id : null,
  )
  const [saving, setSaving] = useState<boolean>(false)
  const [errors, setErrors] = useState<
    Record<string, string> | null | undefined
  >(null)

  const handleSubmit = async () => {
    const errors: Record<string, string> = {}
    if (!name) errors.name = 'Please input a name'
    if (!category) errors.category = 'Please select a category'

    if (Object.keys(errors).length) {
      return setErrors(errors)
    }

    if (name && category) {
      setErrors(null)
      setSaving(true)

      try {
        if (editingTag) {
          const data = {
            category_id: category,
            name,
          }
          await dispatch(editCustomNativeTag(editingTag.id, data))
        } else {
          await dispatch(createCustomNativeTag(name, category))
        }

        onRequestClose()
      } catch (error) {}

      setSaving(false)
    }
  }

  return (
    <ModalDialog
      title={editingTag ? 'Edit track tag' : 'Create track tag'}
      primaryAction={{
        caption: editingTag ? 'Save' : 'Create',
        onClick: handleSubmit,
        disabled: editingTag
          ? editingTag?.name === name && editingTag?.category_id === category
          : !name,
        progressCaption: editingTag ? 'Saving' : 'Creating',
        progressIndicator: saving,
      }}
      onCancel={onRequestClose}
    >
      {!colorIndicatorsEnabled && (
        <Box mb="4">
          <ColorIndicatorsAlert />
        </Box>
      )}
      <Box mb="4">
        <TextField
          autoFocus
          error={errors?.name}
          label="Tag name"
          placeholder="Enter tag name"
          value={name}
          onChange={event => setName(event.target.value)}
        />
      </Box>
      <Box mb="4">
        <Label caption="Select category" validationError={errors?.category} />
        <CustomDropdown
          options={nativeCategories.map(category => ({
            label: category.name,
            value: category.id,
          }))}
          value={category}
          onChange={option =>
            setCategory(option == null ? null : Number(option.value))
          }
        />
      </Box>
    </ModalDialog>
  )
}

export default TrackTagEditModal
